/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useState} from "react";
import clsx from "clsx";
import {useTranslation} from "react-i18next";
import PlusIcon from "components/Icons/FontAwesome/PlusIcon";
import MinusIcon from "components/Icons/FontAwesome/MinusIcon";
import {floatNumberFormat} from "utils/utility";
import {CURRENCY} from "helpers/config";
import {useDebouncedCallback} from "use-debounce";

interface GiftCardArticleCardCustomProps {
    onClick: (price: number) => void;
}

const GiftCardArticleCardCustom = (props: GiftCardArticleCardCustomProps) => {
    const {t} = useTranslation();
    const classNameButton =
        "flex items-center justify-center p-2 lg:p-3 xl:p-4 bg-gradient-to-l from-[#F7C12A] to-[#ED3D34] hover:text-white text-black rounded-xl lg:rounded-2xl";
    const classNameButtonIcon = "w-6 h-6 lg:w-7 lg:h-7 xl:w-8 xl:h-8 flex-none";
    const minPrice = 4000;
    const maxPrice = 99900;
    const [price, setPrice] = useState<number>(minPrice);
    const [internalValue, setInternalValue] = useState<number>(minPrice);

    const generatePrice = (newPrice: number) => {
        setPrice(prev => {
            if (newPrice >= maxPrice) {
                setInternalValue(maxPrice);
                return maxPrice;
            }
            if (newPrice <= minPrice) {
                setInternalValue(minPrice);
                return minPrice;
            }
            const roundedPrice = Math.ceil(newPrice / 100 / 5) * 5 * 100;
            setInternalValue(roundedPrice);
            return roundedPrice;
        });
    };

    const debouncedChangeHandler = useDebouncedCallback(data => {
        generatePrice(data);
    }, 500);

    const setPriceHandler = (state: "down" | "up") => {
        const newPrice = state === "down" ? price - 500 : price + 500;
        generatePrice(newPrice);
    };

    const onChangeHandler = (newPrice: string) => {
        const convertedPrice = +newPrice * 100;
        setInternalValue(convertedPrice);
        debouncedChangeHandler(convertedPrice);
    };

    return (
        <div className={"ArticleCard font-zuume flex flex-col justify-center items-center mx-auto w-auto"}>
            <div className={"w-full sm:w-auto"}>
                <div className={"select-none rounded-2xl gap-y-2 flex flex-col leading-[1] text-center mx-auto"}>
                    <div className={" italic font-black text-5xl md:text-6xl mb-3"}>{t("Custom price")}</div>
                    <div
                        className={
                            "text-black cursor-pointer rounded-2xl text-sm bg-gradient-to-b from-[#f7f7f7] to-white pt-4 pb-3 sm:pt-3 sm:pb-2 lg:pt-5 lg:pb-4 capitalize font-semibold px-2 lg:px-4 h-full flex flex-row space-x-6 justify-between items-center"
                        }
                    >
                        <button className={classNameButton} type={"button"} onClick={() => setPriceHandler("down")}>
                            <MinusIcon className={classNameButtonIcon} />
                        </button>
                        <div className={"flex items-center text-4xl sm:text-4xl md:text-4xl lg:text-5xl xl:text-[40pt] font-bold italic space-x-1"}>
                            <span>{CURRENCY}</span>
                            <input
                                className={
                                    "text-center  bg-transparent appearance-none pl-1 no-arrows max-w-[46px] lg:max-w-[60px] xl:max-w-[68px] text-4xl sm:text-4xl md:text-4xl lg:text-5xl xl:text-[40pt] font-bold italic inline-block flex-shrink-0 w-auto"
                                }
                                step={5}
                                id={"price"}
                                type="number"
                                value={floatNumberFormat(internalValue, 0)}
                                onChange={e => onChangeHandler(e.target.value)}
                            />
                            <span>.-</span>
                        </div>
                        <button className={classNameButton} type={"button"} onClick={() => setPriceHandler("up")}>
                            <PlusIcon className={classNameButtonIcon} />
                        </button>
                    </div>
                    <button
                        className={clsx(
                            "ButtonCTA duration-150 transform-gpu",
                            "font-poppins rounded-2xl hover:drop-shadow-none drop-shadow-[0px_5px_5px_rgba(0,0,0,0.20)] mix-blend-multiply bg-gradient-to-l from-[#F7C12A] to-[#ED3D34] hover:text-white text-black text-base  lg:text-lg xl:text-xl py-3 px-6 font-bold"
                        )}
                        onClick={() => props.onClick(price)}
                    >
                        <span>{t("Add to cart")}</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default GiftCardArticleCardCustom;
